import React from "react";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { useTermsViewModel } from "@/features/terms/viewmodel/useTermsViewModel";
import { useUnit } from "effector-react";
import {
  $countriesTerms,
  $sectorsTerms,
  $programmeTerms,
  $expertiseTerms,
  $organizationCategoriesTerms,
  $organizationEmployeesNumberTerms,
} from "@/core/store/terms";
import "./SelectedFilters.scss";

export interface SelectedFiltersProps {
  filters: {
    countries: string[];
    sectors: string[];
    programmes: string[];
    organizationCategories: string[];
    companySize: string[];
    legalForm: string[];
    expertise: string[];
  };
  onRemoveFilter: (category: string, value: string) => void;
  onClearAll: () => void;
}

export const SelectedFilters: React.FC<SelectedFiltersProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
}) => {
  const termsStore = useTermsViewModel();
  const [
    countriesTerms,
    sectorsTerms,
    programmeTerms,
    expertiseTerms,
    organizationCategoriesTerms,
    organizationEmployeesNumberTerms,
  ] = useUnit([
    $countriesTerms,
    $sectorsTerms,
    $programmeTerms,
    $expertiseTerms,
    $organizationCategoriesTerms,
    $organizationEmployeesNumberTerms,
  ]);

  // Helper function to get filter name from terms store
  const getFilterName = (category: string, value: string): string => {
    switch (category) {
      case "countries":
        return (
          countriesTerms.data?.find((term) => term.tid === value)?.name || value
        );
      case "sectors":
        return (
          termsStore.sectors.find((term) => term.tid === value)?.name || value
        );
      case "programmes":
        return (
          programmeTerms.data?.find((term) => term.tid === value)?.name || value
        );
      case "expertise":
        return (
          expertiseTerms.data?.find((term) => term.tid === value)?.name || value
        );
      case "organizationCategories":
        return (
          organizationCategoriesTerms.data?.find((term) => term.tid === value)
            ?.name || value
        );
      case "companySize":
        return (
          organizationEmployeesNumberTerms.data?.find(
            (term) => term.tid === value,
          )?.name || value
        );
      default:
        return value;
    }
  };

  // Get all active filters
  const activeFilters: Array<{
    category: string;
    value: string;
    label: string;
    displayName: string;
  }> = [];

  Object.entries(filters).forEach(([category, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      values.forEach((value) => {
        activeFilters.push({
          category,
          value,
          label: getCategoryLabel(category),
          displayName: getFilterName(category, value),
        });
      });
    }
  });

  // Helper function to get human-readable category names
  function getCategoryLabel(category: string): string {
    const categoryLabels: Record<string, string> = {
      countries: "Country",
      sectors: "Sector",
      programmes: "Programme",
      organizationCategories: "Organization",
      companySize: "Company Size",
      legalForm: "Legal Form",
    };
    return categoryLabels[category] || category;
  }

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="selected-filters">
      <div className="selected-filters-header">
        {/* <span className="filters-count">
          {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} applied
        </span> */}
        {/* <button className="clear-all-btn" onClick={onClearAll}>
          <SvgIcon icon="icon-close" />
          Clear all
        </button> */}
      </div>

      <div className="filters-list">
        {activeFilters.map((filter, index) => (
          <div
            key={`${filter.category}-${filter.value}-${index}`}
            className="filter-tag"
          >
            <span className="filter-category">{filter.label}:</span>
            <span className="filter-value">
              {filter.label === "Legal Form"
                ? filter.displayName === "0"
                  ? "Private"
                  : "Public"
                : filter.displayName}
            </span>
            <button
              className="remove-filter-btn"
              onClick={() => onRemoveFilter(filter.category, filter.value)}
              aria-label={`Remove ${filter.displayName} filter`}
            >
              <SvgIcon icon="icon-close" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
